import { isPlatformBrowser } from '@angular/common';
import { APP_INITIALIZER, Optional, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';
import { ApiService } from 'app/core/services/api.service';
import { KbService } from 'app/core/services/kb.service';
import { NetworkService } from 'app/core/services/network.service';
import { SSR_REQUEST_URL } from 'app/core/tokens/ssr-request-url.token';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

export interface KbPortalSsrState {
    kbSlugDetails: any | null;
    kbSettings: any | null;
    categoriesResponse: any | null;
    slug: string | null;
    tenantName?: string | null;
}

export const KB_PORTAL_SSR_STATE_KEY = makeStateKey<KbPortalSsrState>('kbPortalSsr');

function shouldSkipPortalPrefetch(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path.startsWith('/404') || path.startsWith('/500');
}

function parsePortalRouteParams(url: string): { slug: string | null; lang: string | null } {
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);

    if (segments.length === 0) {
        return { slug: null, lang: null };
    }

    if (segments[0] === 'customer-portal') {
        segments.shift();
    }

    if (segments.length === 0) {
        return { slug: null, lang: null };
    }

    if (segments[0] === 'article') {
        return { slug: null, lang: null };
    }

    const reserved = new Set(['category', 'article', '404', '500']);
    if (reserved.has(segments[0])) {
        return { slug: null, lang: null };
    }

    const slug = segments[0] ?? null;
    let lang: string | null = null;

    if (segments[1] && !reserved.has(segments[1])) {
        lang = segments[1];
    }

    return { slug, lang };
}

function applyPortalSsrState(
    state: KbPortalSsrState,
    kbSer: KbService,
    api: ApiService,
    transferState: TransferState,
    persistToTransferState: boolean,
): void {
    if (state.tenantName) {
        api.setBaseUrl(state.tenantName);
        api.tenantHostname = state.tenantName;
    }

    kbSer.hydrateFromPortalSsrState(state);

    if (persistToTransferState) {
        transferState.set(KB_PORTAL_SSR_STATE_KEY, state);
    }
}

export function kbPortalSsrInitializerFactory(
    platformId: object,
    request: Request | null,
    ssrRequestUrl: string | null,
    transferState: TransferState,
    kbSer: KbService,
    api: ApiService,
    network: NetworkService,
) {
    return () => {
        if (isPlatformBrowser(platformId)) {
            const cached = transferState.get(KB_PORTAL_SSR_STATE_KEY, null);
            if (cached) {
                applyPortalSsrState(cached, kbSer, api, transferState, false);
                transferState.remove(KB_PORTAL_SSR_STATE_KEY);
            }

            if (api.tenantHostname) {
                return Promise.resolve();
            }

            return firstValueFrom(
                api.getDomainConfig().pipe(catchError(() => of(null))),
            ).then(() => undefined);
        }

        const requestUrl = ssrRequestUrl || request?.originalUrl || request?.url;
        if (!requestUrl || shouldSkipPortalPrefetch(requestUrl)) {
            return Promise.resolve();
        }

        const routeParams = parsePortalRouteParams(requestUrl);

        return firstValueFrom(
            kbSer.getKbslugDetails().pipe(
                switchMap((details) => {
                    const slug = routeParams.slug ?? details?.default_kb_slug ?? null;
                    const lang = routeParams.lang ?? details?.default_language ?? null;

                    if (!slug) {
                        return of(null);
                    }

                    return forkJoin({
                        kbSettings: api.getData(`kb-page-builder/${slug}`),
                        categoriesResponse: lang
                            ? network.getKbArticlesCategory({
                                  kb_slug: slug,
                                  kb_lang: lang,
                              })
                            : of(null),
                    }).pipe(
                        map(({ kbSettings, categoriesResponse }) => ({
                            kbSlugDetails: details,
                            kbSettings,
                            categoriesResponse,
                            slug,
                            tenantName: api.tenantHostname,
                        })),
                    );
                }),
                tap((state) => {
                    if (!state) {
                        return;
                    }

                    if (state.kbSettings) {
                        applyPortalSsrState(state, kbSer, api, transferState, true);
                        return;
                    }

                    if (state.tenantName || state.kbSlugDetails) {
                        applyPortalSsrState(
                            {
                                kbSlugDetails: state.kbSlugDetails,
                                kbSettings: null,
                                categoriesResponse: null,
                                slug: state.slug,
                                tenantName: state.tenantName,
                            },
                            kbSer,
                            api,
                            transferState,
                            true,
                        );
                    }
                }),
                catchError(() => of(null)),
            ),
        ).then(() => undefined);
    };
}

export const KB_PORTAL_SSR_INITIALIZER = {
    provide: APP_INITIALIZER,
    useFactory: kbPortalSsrInitializerFactory,
    deps: [
        PLATFORM_ID,
        [new Optional(), REQUEST],
        [new Optional(), SSR_REQUEST_URL],
        TransferState,
        KbService,
        ApiService,
        NetworkService,
    ],
    multi: true,
};
